// Declarative <map-box> layers.
//
// Each layer element is a child of <map-box>; on connect it fires
// "map-layer:connected" on the eventbus so the parent <map-box> can call
// connectToMap(map) once the OL Map is ready.
//
//   <map-box>
//     <map-vector-layer geojson-url="..." min-zoom="17" max-zoom="30"></map-vector-layer>
//     <map-vector-layer geojson-url="..." heatmap weight-property="quantity"></map-vector-layer>
//     <map-gpx-layer gpx-url="..."></map-gpx-layer>
//   </map-box>
//
// The legacy attributes geojson-url / gpx-url directly on <map-box> remain
// as a thin shim handled inside MapBox.setup() (with the generic styler —
// no domain-specific colours or heatmap weights).

import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import HeatmapLayer from 'ol/layer/Heatmap.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import GPX from 'ol/format/GPX.js';
import {get as getProj, transformExtent} from 'ol/proj.js';
import {bbox as bboxStrategy} from 'ol/loadingstrategy.js';

import { mapStyle, styleResolver } from './map-styles.js';


function dispatchConnected(target) {
  target.dispatchEvent(new CustomEvent('map-layer:connected', {
    bubbles:    true,
    cancelable: false,
  }));
}


export class MapVectorLayer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    dispatchConnected(this);
  }

  disconnectedCallback() {
    if (this.map && this.layer) {
      this.map.removeLayer(this.layer);
    }
  }

  connectToMap(map) {
    this.map = map;
    const url = this.getAttribute('geojson-url');
    if (!url) return;

    const source = new VectorSource({
      loader: (extent, _resolution, projection, success, failure) => {
        const extentString = transformExtent(extent, projection, getProj('EPSG:4326')).join(',');
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${url}?bbox=${extentString}`);
        xhr.setRequestHeader('Accept', 'application/geo+json')
        xhr.onerror = () => { source.removeLoadedExtent(extent); failure(); };
        xhr.onload  = () => {
          if (xhr.status === 200) {
            const features = source.getFormat().readFeatures(JSON.parse(xhr.responseText));
            source.addFeatures(features);
            success(features);
          } else {
            source.removeLoadedExtent(extent);
            failure();
          }
        };
        xhr.send();
      },
      strategy: bboxStrategy,
      format:   new GeoJSON({
        dataProjection:    getProj('EPSG:4326'),
        featureProjection: getProj('EPSG:3857'),
      }),
    });

    if (this.hasAttribute('heatmap')) {
      const weightProp = this.getAttribute('weight-property');
      const divisor    = parseFloat(this.getAttribute('weight-divisor') || '1');
      const layer = new HeatmapLayer({
        source,
        minZoom: parseInt(this.getAttribute('heatmap-min-zoom') || '10', 10),
        maxZoom: parseInt(this.getAttribute('heatmap-max-zoom') || '18', 10),
        weight:  weightProp
          ? (feature) => (feature.get(weightProp) || 0) / divisor
          : (_feature) => 1,
      });
      this.map.addLayer(layer);
      // We add both heatmap and vector layers; the heatmap takes lower zooms,
      // the vector takes higher zooms.
    }

    const styler = styleResolver(this.getAttribute('style-key'));
    this.layer = new VectorLayer({
      source,
      minZoom: this.hasAttribute('min-zoom') ? parseInt(this.getAttribute('min-zoom'), 10) : undefined,
      maxZoom: this.hasAttribute('max-zoom') ? parseInt(this.getAttribute('max-zoom'), 10) : undefined,
      style:   styler,
    });
    this.map.addLayer(this.layer);
  }
}


export class MapGpxLayer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    dispatchConnected(this);
  }

  disconnectedCallback() {
    if (this.map && this.layer) {
      this.map.removeLayer(this.layer);
    }
  }

  connectToMap(map) {
    this.map = map;
    const url = this.getAttribute('gpx-url');
    if (!url) return;

    this.layer = new VectorLayer({
      source: new VectorSource({url, format: new GPX()}),
      style:  (feature) => mapStyle[feature.getGeometry().getType()],
    });
    this.map.addLayer(this.layer);
  }
}
